import { extractToolCallName } from "./toolCallName"

const DTMF_KEY_PATTERN = /[0-9*#A-D]/g
const DTMF_TOOL_NAME_PATTERN = /dtmf/i

const parseRecord = (content: unknown): Record<string, unknown> | null => {
  if (typeof content === "string") {
    try {
      return parseRecord(JSON.parse(content))
    } catch {
      return null
    }
  }
  if (typeof content === "object" && content !== null) {
    return content as Record<string, unknown>
  }
  return null
}

const readDigitKeys = (value: unknown): string[] => {
  if (typeof value === "string") {
    return value.toUpperCase().match(DTMF_KEY_PATTERN) ?? []
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => readDigitKeys(item))
  }
  return []
}

const readDigits = (container: unknown): string[] => {
  const record = parseRecord(container)
  return record ? readDigitKeys(record.digits) : []
}

/**
 * Returns the keypad keys a DTMF tool call pressed, or null when the content
 * is not a DTMF tool call or carries no digits.
 *
 * Accepts `digits` as a string (Telnyx, scripted) or a list (Twilio,
 * websocket), on `arguments` (or the OpenAI-style `function.arguments`
 * JSON string) first and `result` as a fallback. Non-key
 * characters such as dial-string `w` pauses or phone-number formatting are
 * dropped.
 */
export const extractDtmfDigits = (content: unknown): string[] | null => {
  const record = parseRecord(content)
  if (!record) {
    return null
  }

  const name = extractToolCallName(content)
  if (!name || !DTMF_TOOL_NAME_PATTERN.test(name)) {
    return null
  }

  const toolFunction = parseRecord(record.function)
  const digits = [
    record.arguments,
    toolFunction?.arguments,
    record.result,
  ].reduce<string[]>(
    (found, source) => (found.length > 0 ? found : readDigits(source)),
    [],
  )

  return digits.length > 0 ? digits : null
}
